import { format } from 'date-fns';
import { MinDto, NamedEntityMinDto } from 'types/other';
import { LessonEntry } from './components/LessonsComposer';
import { CreateOrUpdateDebriefing, DebriefingDetails, DebriefingPersonRef, DebriefingType } from './types';

/** The matter shape the case picker provides / the form needs; synthesized on edit from the
 * stored refs (which carry no client/employee data). */
export interface DebriefingFormMatter {
    id: number;
    caseNumber?: string;
    name?: string;
    fullName: string;
    isConfidential?: boolean | null;
    clientParty?: MinDto | null;
    supervisedByEmployee?: MinDto | null;
    responsibleEmployee?: MinDto | null;
}

export interface DebriefingFormValues {
    debriefingType: DebriefingType;
    debriefingDate: string | null;
    matters: DebriefingFormMatter[];
    clientParty: MinDto | null;
    projectName: string;
    isConfidential: boolean;
    participants: MinDto[];
    sessionLeader: MinDto | null;
    supervisedBy: MinDto[];
    responsibleLawyers: MinDto[];
    relationshipPartners: MinDto[];
    relationshipManagers: MinDto[];
    context: string;
    isClientRepPresent: boolean;
    clientRepresentedBy: MinDto | null;
    clientFeedback: string;
    feedbackGivers: MinDto[];
    scoreScopeMgmt: number | null;
    scoreTeamComposition: number | null;
    scoreDeadlinesMgmt: number | null;
    scoreBudgetMgmt: number | null;
    scoreCommunication: number | null;
    scoreGeneralPerformance: number | null;
    lessons: LessonEntry[];
}

export function emptyDebriefingForm(): DebriefingFormValues {
    return {
        debriefingType: DebriefingType.CASE_SPECIFIC,
        debriefingDate: format(new Date(), 'yyyy-MM-dd'),
        matters: [],
        clientParty: null,
        projectName: '',
        isConfidential: false,
        participants: [],
        sessionLeader: null,
        supervisedBy: [],
        responsibleLawyers: [],
        relationshipPartners: [],
        relationshipManagers: [],
        context: '',
        isClientRepPresent: false,
        clientRepresentedBy: null,
        clientFeedback: '',
        feedbackGivers: [],
        scoreScopeMgmt: null,
        scoreTeamComposition: null,
        scoreDeadlinesMgmt: null,
        scoreBudgetMgmt: null,
        scoreCommunication: null,
        scoreGeneralPerformance: null,
        lessons: [],
    };
}

function toMinDto(ref: DebriefingPersonRef): MinDto {
    return { id: ref.id, name: ref.name };
}

export function detailsToForm(details: DebriefingDetails): DebriefingFormValues {
    return {
        // A draft saved through the API may have no type; the form always holds one.
        debriefingType: details.debriefingType ?? DebriefingType.CASE_SPECIFIC,
        debriefingDate: details.debriefingDate,
        matters: (details.matters ?? []).map((matter) => ({
            id: matter.id,
            caseNumber: matter.caseNumber,
            name: matter.name,
            fullName: [matter.caseNumber, matter.name].filter(Boolean).join(' '),
            isConfidential: matter.isConfidential,
        })),
        clientParty: details.clientParty ? toMinDto(details.clientParty) : null,
        projectName: details.projectName ?? '',
        isConfidential: !!details.isConfidential,
        participants: (details.participants ?? []).map(toMinDto),
        sessionLeader: details.sessionLeader ? toMinDto(details.sessionLeader) : null,
        supervisedBy: (details.supervisedBy ?? []).map(toMinDto),
        responsibleLawyers: (details.responsibleLawyers ?? []).map(toMinDto),
        relationshipPartners: (details.relationshipPartners ?? []).map(toMinDto),
        relationshipManagers: (details.relationshipManagers ?? []).map(toMinDto),
        context: details.context ?? '',
        isClientRepPresent: !!details.isClientRepPresent,
        clientRepresentedBy: details.clientRepresentedBy ? toMinDto(details.clientRepresentedBy) : null,
        clientFeedback: details.clientFeedback ?? '',
        feedbackGivers: (details.feedbackGivers ?? []).map(toMinDto),
        scoreScopeMgmt: details.scoreScopeMgmt,
        scoreTeamComposition: details.scoreTeamComposition,
        scoreDeadlinesMgmt: details.scoreDeadlinesMgmt,
        scoreBudgetMgmt: details.scoreBudgetMgmt,
        scoreCommunication: details.scoreCommunication,
        scoreGeneralPerformance: details.scoreGeneralPerformance,
        lessons: (details.lessons ?? []).map((lesson) => ({
            id: lesson.id,
            lessonType: lesson.lessonType,
            description: lesson.description,
            relevance: lesson.relevance,
            categoryCodes: lesson.categoryCodes,
        })),
    };
}

/** Strips fields the BE rejects outside their mode; a switched-away mode's values never leak. */
export function formToDto(form: DebriefingFormValues): CreateOrUpdateDebriefing {
    const isCase = form.debriefingType === DebriefingType.CASE_SPECIFIC;
    const isClient = form.debriefingType === DebriefingType.CLIENT_SPECIFIC;
    const isNonClient = form.debriefingType === DebriefingType.NON_CLIENT;
    const ids = (values: MinDto[]) => values.map(({ id }) => id);
    return {
        debriefingType: form.debriefingType,
        debriefingDate: form.debriefingDate ?? undefined,
        matterIds: isCase ? form.matters.map(({ id }) => id) : undefined,
        clientPartyId: isClient ? (form.clientParty?.id ?? undefined) : undefined,
        projectName: isNonClient && form.projectName.trim() ? form.projectName.trim() : undefined,
        sessionLeaderEmployeeId: form.sessionLeader?.id,
        participantEmployeeIds: ids(form.participants),
        supervisedByEmployeeIds: isCase ? ids(form.supervisedBy) : undefined,
        responsibleLawyerEmployeeIds: isCase ? ids(form.responsibleLawyers) : undefined,
        relationshipPartnerEmployeeIds: isClient ? ids(form.relationshipPartners) : undefined,
        relationshipManagerEmployeeIds: isClient ? ids(form.relationshipManagers) : undefined,
        context: form.context.trim() || undefined,
        isConfidential: form.isConfidential,
        isClientRepPresent: !isNonClient && form.isClientRepPresent,
        clientRepresentedByPartyId:
            !isNonClient && form.isClientRepPresent ? (form.clientRepresentedBy?.id ?? undefined) : undefined,
        clientFeedback: !isNonClient && form.clientFeedback.trim() ? form.clientFeedback.trim() : undefined,
        feedbackGiverPartyIds: isNonClient ? undefined : ids(form.feedbackGivers),
        scoreScopeMgmt: form.scoreScopeMgmt,
        scoreTeamComposition: form.scoreTeamComposition,
        scoreDeadlinesMgmt: form.scoreDeadlinesMgmt,
        scoreBudgetMgmt: form.scoreBudgetMgmt,
        scoreCommunication: form.scoreCommunication,
        scoreGeneralPerformance: form.scoreGeneralPerformance,
        lessons: form.lessons.map((lesson) => ({
            lessonType: lesson.lessonType,
            description: lesson.description,
            relevance: lesson.relevance,
            categoryCodes: lesson.categoryCodes,
        })),
    };
}

/**
 * The form fields each card of the record page edits. A card keeps a driver with what it drives: the type, cases and
 * client with the owners they derive, the participants with their leader.
 */
export const DEBRIEFING_SECTION_FIELDS = {
    debriefing: [
        'debriefingType',
        'isConfidential',
        'debriefingDate',
        'matters',
        'clientParty',
        'projectName',
        'supervisedBy',
        'responsibleLawyers',
        'relationshipPartners',
        'relationshipManagers',
        'context',
    ],
    participants: ['participants', 'sessionLeader'],
    'client-feedback': ['isClientRepPresent', 'clientRepresentedBy', 'clientFeedback', 'feedbackGivers'],
    'self-evaluation': [
        'scoreScopeMgmt',
        'scoreTeamComposition',
        'scoreDeadlinesMgmt',
        'scoreBudgetMgmt',
        'scoreCommunication',
        'scoreGeneralPerformance',
    ],
    lessons: ['lessons'],
} satisfies Record<string, (keyof DebriefingFormValues)[]>;

export type DebriefingSectionId = keyof typeof DEBRIEFING_SECTION_FIELDS;

export function isDebriefingFormValid(form: DebriefingFormValues): boolean {
    const modeIdentityValid =
        form.debriefingType === DebriefingType.CASE_SPECIFIC
            ? form.matters.length > 0
            : form.debriefingType === DebriefingType.CLIENT_SPECIFIC
              ? !!form.clientParty
              : !!form.projectName.trim();
    const leaderValid =
        !!form.sessionLeader && form.participants.some((participant) => participant.id === form.sessionLeader?.id);
    const clientRepValid = !form.isClientRepPresent || !!form.clientRepresentedBy;
    return !!form.debriefingDate && modeIdentityValid && form.participants.length > 0 && leaderValid && clientRepValid;
}

export function dedupeById<T extends { id: number }>(values: T[]): T[] {
    const seen = new Set<number>();
    return values.filter(({ id }) => (seen.has(id) ? false : (seen.add(id), true)));
}

/**
 * Lifts the priority colleagues to the head of the loaded window so the two participant groups stay
 * contiguous — dropdown grouping keys off adjacency, not off the group name. Priority colleagues the
 * dropdown has not paged in yet are inserted up front, filtered by the active search so they cannot
 * reappear once the user has narrowed the list past them.
 */
export function orderByPriority<T extends MinDto>(options: T[], priority: NamedEntityMinDto[], search: string): T[] {
    if (priority.length === 0) {
        return options;
    }
    const priorityIds = new Set(priority.map(({ id }) => id));
    const term = search.trim().toLowerCase();
    const loadedPriority = options.filter((option) => priorityIds.has(option.id));
    const loadedIds = new Set(loadedPriority.map(({ id }) => id));
    // The name parts ride along so an injected option formats the same way as a paged one under
    // the last-name-first setting.
    const missingPriority = priority
        .filter(({ id, name }) => !loadedIds.has(id) && name.toLowerCase().includes(term))
        .map(({ id, name, firstName, lastName }) => ({ id, name, firstName, lastName }) as unknown as T);
    return [...loadedPriority, ...missingPriority, ...options.filter((option) => !priorityIds.has(option.id))];
}

/**
 * Distinct clients across a case selection. Matters synthesized on edit carry no client of their own,
 * so the debriefing's already-derived client stands in for them.
 */
export function distinctClientIds(matters: DebriefingFormMatter[], derivedClientId: number | null): number[] {
    const ids = new Set<number>();
    matters.forEach((matter) => {
        const clientId = matter.clientParty?.id ?? derivedClientId;
        if (clientId != null) {
            ids.add(clientId);
        }
    });
    return [...ids];
}

/** A linked confidential case locks the checkbox and forbids adding further cases. */
export function hasConfidentialMatter(matters: DebriefingFormMatter[]): boolean {
    return matters.some((matter) => matter.isConfidential);
}

/** Union of the selected cases' supervising/responsible lawyers, deduplicated, order-preserving. */
export function deriveCaseOwners(matters: DebriefingFormMatter[]): {
    supervisedBy: MinDto[];
    responsibleLawyers: MinDto[];
} {
    const dedupe = (values: (MinDto | null | undefined)[]): MinDto[] => {
        const seen = new Set<number>();
        return values.flatMap((value) => {
            if (!value || seen.has(value.id)) {
                return [];
            }
            seen.add(value.id);
            return [value];
        });
    };
    return {
        supervisedBy: dedupe(matters.map((matter) => matter.supervisedByEmployee)),
        responsibleLawyers: dedupe(matters.map((matter) => matter.responsibleEmployee)),
    };
}
